import { createContext, useRef, useState, useCallback, useEffect, useMemo, createElement, useContext, Fragment } from 'react';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

var announcerContextStyles = {
  announceMessage: {
    position: 'absolute',
    left: '-100%',
    top: '-100%',
    opacity: '0',
    pointerEvents: 'none'
  }
};

var AnnounceType;

(function (AnnounceType) {
  AnnounceType["Assertive"] = "assertive";
  AnnounceType["Polite"] = "polite";
})(AnnounceType || (AnnounceType = {}));

var AnnouncerContext = /*#__PURE__*/createContext({
  announce: function announce() {
    return;
  }
});
var AnnouncerContextProvider = function AnnouncerContextProvider(_a) {
  var children = _a.children;
  var timeout = useRef(undefined);

  var _b = __read(useState({
    message: undefined,
    announceType: AnnounceType.Polite
  }), 2),
      announceState = _b[0],
      setAnnounceState = _b[1];

  var message = announceState.message,
      announceType = announceState.announceType;
  var announce = useCallback(function (newMessage, newAnnounceType) {
    if (newAnnounceType === void 0) {
      newAnnounceType = AnnounceType.Polite;
    }

    setAnnounceState({
      message: newMessage,
      announceType: newAnnounceType
    });
  }, []);
  useEffect(function () {
    if (message) {
      timeout.current && window.clearTimeout(timeout.current); // Flip the message to empty so that screen readers will announce consecutive announcements that are the same

      timeout.current = window.setTimeout(function () {
        setAnnounceState(function (prevState) {
          return __assign(__assign({}, prevState), {
            message: ''
          });
        });
      }, 100);
    } // clear timeout if exists when tearing down the component


    return function () {
      window.clearTimeout(timeout.current);
    };
  }, [message]);
  var providerValue = useMemo(function () {
    return {
      announce: announce
    };
  }, [announce]);
  return /*#__PURE__*/createElement(AnnouncerContext.Provider, {
    value: providerValue
  }, children, /*#__PURE__*/createElement("span", {
    "aria-atomic": true,
    role: "status",
    "aria-live": "polite",
    style: announcerContextStyles.announceMessage
  }, announceType === AnnounceType.Polite && message), /*#__PURE__*/createElement("span", {
    "aria-atomic": true,
    role: "status",
    "aria-live": "assertive",
    style: announcerContextStyles.announceMessage
  }, announceType === AnnounceType.Assertive && message));
};
var useAnnouncerContext = function useAnnouncerContext() {
  return useContext(AnnouncerContext);
};

var Announce = function Announce(_a) {
  var message = _a.message,
      announceType = _a.announceType,
      props = __rest(_a, ["message", "announceType"]);

  var announcerContext = useAnnouncerContext();
  var announceMessage = useCallback(function (messageToAnnounce) {
    if (messageToAnnounce) {
      announcerContext.announce(messageToAnnounce, announceType || AnnounceType.Polite);
    }
  }, [announceType, announcerContext]);
  useEffect(function () {
    announceMessage(message || props.children);
  }, [announceMessage, message, props.children]); // This is actually needed, because children could be an array
  // eslint-disable-next-line react/jsx-no-useless-fragment

  return /*#__PURE__*/createElement(Fragment, null, props.children);
};

var AnnouncerContext$1 = /*#__PURE__*/createContext({
  announce: function announce() {
    return;
  }
});
var MockedAnnouncerContextProvider = function MockedAnnouncerContextProvider(_a) {
  var children = _a.children;
  var providerValue = useMemo(function () {
    return {
      announce: function announce() {
        return;
      }
    };
  }, []);
  return /*#__PURE__*/createElement(AnnouncerContext$1.Provider, {
    value: providerValue
  }, children);
};

export { Announce, AnnounceType, AnnouncerContext, AnnouncerContextProvider, MockedAnnouncerContextProvider, useAnnouncerContext };
